import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { endpoints, type Meeting, type Project, type ProjectDetail, type ProjectRef } from "../lib/api";
import { platformLabel } from "../lib/calendar";
import { duration } from "../lib/format";
import { PeopleField } from "./PeoplePicker";

/* The dialogs behind projects (pages/ProjectsPage.tsx, pages/ProjectPage.tsx,
 * and "Add to project" on a meeting's page). Drawn at the top of the page, as
 * the Share dialog is: the meeting page is a CSS container, which would trap
 * a fixed overlay inside it. */

function Dialog({ title, onClose, children, wide }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="share-scrim" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className={`share-dialog${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <div className="share-head">
          <h2 id="dialog-title">{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** The domain a name typed on its own belongs to: your own. */
function useDomain(): string {
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  return me.data?.email.split("@")[1] ?? "";
}

/** A new project (name, description, people), or new words for one. */
export function ProjectForm({ project, csrfToken, onClose, onSaved }: {
  project?: Project;
  csrfToken: string;
  onClose: () => void;
  onSaved: (project: ProjectDetail) => void;
}) {
  const queryClient = useQueryClient();
  const domain = useDomain();
  const [name, setName] = useState(project?.name ?? "");
  const [description, setDescription] = useState(project?.description ?? "");
  const [people, setPeople] = useState<string[]>([]);
  const me = useQuery({ queryKey: ["me"], queryFn: endpoints.me, staleTime: Infinity });
  const taken = useMemo(() => new Set(me.data ? [me.data.email.toLowerCase()] : []), [me.data]);

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim() || null };
      return project
        ? endpoints.updateProject(project.id, body, csrfToken)
        : endpoints.createProject({ ...body, emails: people }, csrfToken);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(["project", saved.id], saved);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      onSaved(saved);
    },
  });

  return (
    <Dialog title={project ? "Edit project" : "New project"} onClose={onClose}>
      <form
        className="project-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <label>
          <span>Name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} autoFocus
                 placeholder="Calypto landing page" />
        </label>
        <label>
          <span>Description <small>optional</small></span>
          <textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={300}
                    rows={2} placeholder="Website relaunch and ads" />
        </label>
        {!project && (
          <div className="project-form-people">
            <span>People <small>optional · you can add them later</small></span>
            <PeopleField chosen={people} onChange={setPeople} taken={taken} domain={domain}
                         label="Add people by name or email" />
          </div>
        )}
        {!project && (
          <p className="share-note">Everyone in the project can open every meeting in it.</p>
        )}
        {save.error && <div className="error">{(save.error as Error).message}</div>}
        <div className="dialog-actions">
          <button type="button" className="btn-quiet" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!name.trim() || save.isPending}>
            {save.isPending ? "Saving…" : project ? "Save" : "Create project"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Add colleagues to a project: they can open its meetings straight away. */
export function AddPeople({ project, csrfToken, onClose }: {
  project: ProjectDetail;
  csrfToken: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const domain = useDomain();
  const [people, setPeople] = useState<string[]>([]);
  const taken = useMemo(() => new Set(project.members.map((m) => m.email)), [project.members]);
  const add = useMutation({
    mutationFn: () => endpoints.addProjectMembers(project.id, people, csrfToken),
    onSuccess: (saved) => {
      queryClient.setQueryData(["project", project.id], saved);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      onClose();
    },
  });
  return (
    <Dialog title={`Add people to “${project.name}”`} onClose={onClose}>
      <div className="share-add">
        <PeopleField chosen={people} onChange={setPeople} taken={taken} domain={domain}
                     onSubmit={() => add.mutate()} autoFocus label="Add people by name or email" />
        <button className="btn-primary" disabled={!people.length || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? "Adding…" : "Add"}
        </button>
      </div>
      <p className="share-note">
        They'll be able to open all {project.metrics.meetings} meeting{project.metrics.meetings === 1 ? "" : "s"} in
        this project, and any added later. Only colleagues who have signed in to Meeting Agent can be added.
      </p>
      {add.error && <div className="error">{(add.error as Error).message}</div>}
    </Dialog>
  );
}

/** Pick meetings to put in a project: every recorded meeting you can open,
 * newest first, searchable, several at once. */
export function AddMeetings({ project, csrfToken, onClose }: {
  project: ProjectDetail;
  csrfToken: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const recordings = useQuery({ queryKey: ["recordings"], queryFn: endpoints.recordings });
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const inProject = useMemo(() => new Set(project.meetings.map((m) => m.session_id)), [project.meetings]);

  const meetings = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (recordings.data?.past ?? [])
      .filter((m): m is Meeting & { session_id: string } => Boolean(m.session_id))
      .filter((m) => !needle || [m.title, m.meet_url, m.owner_email].some((t) => t?.toLowerCase().includes(needle)));
  }, [recordings.data, query]);

  const add = useMutation({
    mutationFn: () => endpoints.addProjectMeetings(project.id, picked, csrfToken),
    onSuccess: (saved) => {
      queryClient.setQueryData(["project", project.id], saved);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["recordings"] });
      queryClient.invalidateQueries({ queryKey: ["project-insights", project.id] });
      onClose();
    },
  });

  const toggle = (sessionId: string) =>
    setPicked((was) => (was.includes(sessionId) ? was.filter((s) => s !== sessionId) : [...was, sessionId]));

  return (
    <Dialog title="Add meetings" onClose={onClose} wide>
      <input className="pick-search" type="search" placeholder="Search your meetings" value={query} autoFocus
             onChange={(event) => setQuery(event.target.value)} aria-label="Search your meetings" />
      {recordings.isLoading ? (
        <p className="share-note">Loading…</p>
      ) : meetings.length === 0 ? (
        <p className="share-note">{query.trim() ? "No meetings match." : "You have no recorded meetings yet."}</p>
      ) : (
        <ul className="pick-list">
          {meetings.map((meeting) => {
            const already = inProject.has(meeting.session_id);
            return (
              <li key={meeting.session_id}>
                <label className={already ? "already" : undefined}>
                  <input type="checkbox" checked={already || picked.includes(meeting.session_id)} disabled={already}
                         onChange={() => toggle(meeting.session_id)} />
                  <span className="pick-main">
                    <strong>{meeting.title ?? "Untitled meeting"}</strong>
                    <small>
                      {already ? "Already in this project" : [
                        platformLabel(meeting),
                        meeting.duration_seconds ? duration(meeting.duration_seconds) : null,
                      ].filter(Boolean).join(" · ")}
                    </small>
                  </span>
                  <span className="pick-when">{shortDay(meeting.starts_at ?? meeting.requested_at)}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <p className="share-note">Everyone in the project will be able to open the meetings you add.</p>
      {add.error && <div className="error">{(add.error as Error).message}</div>}
      <div className="dialog-actions">
        <button className="btn-quiet" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!picked.length || add.isPending} onClick={() => add.mutate()}>
          {add.isPending ? "Adding…" : picked.length ? `Add ${picked.length} meeting${picked.length === 1 ? "" : "s"}` : "Add meetings"}
        </button>
      </div>
    </Dialog>
  );
}

/** On a meeting's own page: tick the projects it should be in. Each tick
 * adds or takes it out straight away. */
export function AddToProject({ sessionId, title, inProjects, csrfToken, onClose }: {
  sessionId: string;
  title: string;
  /** The projects it's in now (the meeting page's own data, refreshed after each tick). */
  inProjects: ProjectRef[];
  csrfToken: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const projects = useQuery({ queryKey: ["projects"], queryFn: endpoints.projects });
  const [creating, setCreating] = useState(false);
  const inIds = new Set(inProjects.map((p) => p.id));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["recording", sessionId] });
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["recordings"] });
  };
  const toggle = useMutation({
    mutationFn: ({ id, add }: { id: number; add: boolean }) =>
      add ? endpoints.addProjectMeetings(id, [sessionId], csrfToken) : endpoints.removeProjectMeeting(id, sessionId, csrfToken),
    onSuccess: (saved) => {
      queryClient.setQueryData(["project", saved.id], saved);
      refresh();
    },
  });

  if (creating) {
    return (
      <ProjectForm
        csrfToken={csrfToken}
        onClose={() => setCreating(false)}
        onSaved={(saved) => {
          setCreating(false);
          toggle.mutate({ id: saved.id, add: true });
        }}
      />
    );
  }

  return (
    <Dialog title={`Add “${title}” to a project`} onClose={onClose}>
      {projects.isLoading ? (
        <p className="share-note">Loading…</p>
      ) : (projects.data ?? []).length === 0 ? (
        <p className="share-note">You're not in any projects yet.</p>
      ) : (
        <ul className="pick-list">
          {(projects.data ?? []).map((project) => (
            <li key={project.id}>
              <label>
                <input type="checkbox" checked={inIds.has(project.id)} disabled={toggle.isPending}
                       onChange={(event) => toggle.mutate({ id: project.id, add: event.target.checked })} />
                <span className="pick-main">
                  <strong>{project.name}</strong>
                  <small>
                    {project.members.length} {project.members.length === 1 ? "person" : "people"} · {project.meeting_count} meeting
                    {project.meeting_count === 1 ? "" : "s"}
                  </small>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <p className="share-note">Everyone in a project can open every meeting in it.</p>
      {toggle.error && <div className="error">{(toggle.error as Error).message}</div>}
      <div className="dialog-actions">
        <button className="btn-quiet" onClick={() => setCreating(true)}>+ New project</button>
        <button className="btn-primary" onClick={onClose}>Done</button>
      </div>
    </Dialog>
  );
}

/** "Fri 3 Oct", with the year when it isn't this one. */
export function shortDay(value: string | null | undefined): string {
  if (!value) return "—";
  const day = new Date(value);
  return day.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(day.getFullYear() === new Date().getFullYear() ? {} : { year: "numeric" }),
  });
}
